'use client';
import {
  PointerSensor,
  useSensor,
  useSensors,
  DndContext,
  Collision,
  MeasuringStrategy,
  Active,
  Over,
} from '@dnd-kit/core';
import React from 'react';
import {
  AND_NEW_ID,
  EMPTY_EXPRESSION_ID,
  EXPRESSION_NEW_ID,
  IdTreeExpression,
  NOT_NEW_ID,
  OR_NEW_ID,
  TreeExpression,
} from './entities';
import { IdTreeToTree, TreeToIdTree } from './helpers';
import {
  attachNode,
  cloneTree,
  detachNode,
  findNode,
  getParentRelativeIndex,
  ghostNode,
  ghostRoot,
  newAndNode,
  newIncompleteExpressionNode,
  newNotNode,
  newOrNode,
  tryGetNodeDepth,
  unGhostRoot,
} from './TreeOperations';
import { noop } from '@benzinga/utils';

export interface ExpressionBuilderContextProps {
  expression: TreeExpression | undefined;
  onExpressionUpdate: (expression: TreeExpression | undefined) => void;
}
export interface ExpressionBuilderContextValue {
  displayedRoot: IdTreeExpression | undefined;
  actualRoot: IdTreeExpression | undefined;
  draggedNode: IdTreeExpression | undefined;
  setDisplayedRoot: (root: IdTreeExpression | undefined) => void;
  setActualRoot: (root: IdTreeExpression | undefined) => void;
  applyExpression: (root: IdTreeExpression | undefined) => void;
}

export const ExpressionBuilderContext = React.createContext<ExpressionBuilderContextValue>({
  actualRoot: undefined,
  applyExpression: noop,
  displayedRoot: undefined,
  draggedNode: undefined,
  setActualRoot: noop,
  setDisplayedRoot: noop,
});

export type ExpressionBuilderContextProviderProps = React.PropsWithChildren<ExpressionBuilderContextProps>;

export const ExpressionBuilderContextProvider: React.FC<ExpressionBuilderContextProviderProps> = props => {
  // displayedRoot is used to display items in UI, along with ghost nodes that are previews of changes
  const [displayedRoot, setDisplayedRoot] = React.useState<IdTreeExpression | undefined>(undefined);
  // actualRoot is used to track original state of the tree before drag and drop is finalized
  const [actualRoot, setActualRootInternal] = React.useState<IdTreeExpression | undefined>(undefined);

  const [draggedNode, setDraggedNode] = React.useState<IdTreeExpression | undefined>(undefined);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 10000,
      },
    }),
  );

  const measuring = React.useMemo(
    () => ({
      droppable: {
        strategy: MeasuringStrategy.Always,
      },
    }),
    [],
  );

  const reset = React.useCallback(() => {
    setDraggedNode(undefined);
    setDisplayedRoot(cloneTree(actualRoot));
  }, [actualRoot]);

  const setActualRoot = React.useCallback(
    (root: IdTreeExpression | undefined) => {
      // we need to update at the exact same time.
      // Cannot rely on useEffect since you may hover over something that doesn't exist for one frame before useEffect fires
      setActualRootInternal(cloneTree(root));
      setDisplayedRoot(cloneTree(root));
    },
    [setActualRootInternal],
  );

  React.useEffect(() => {
    setActualRoot(TreeToIdTree(props.expression));
  }, [props.expression, setActualRoot]);

  return (
    <ExpressionBuilderContext.Provider
      value={React.useMemo(() => {
        const onExpressionUpdate = props.onExpressionUpdate;
        return {
          actualRoot,
          applyExpression: (root: IdTreeExpression | undefined) => {
            onExpressionUpdate(IdTreeToTree(root));
          },
          displayedRoot,
          draggedNode,
          setActualRoot,
          setDisplayedRoot,
        };
      }, [actualRoot, displayedRoot, draggedNode, props.onExpressionUpdate, setActualRoot])}
    >
      <DndContext
        collisionDetection={({ active, droppableRects }) => {
          const collisions: Collision[] = [];
          droppableRects.forEach((rect, id) => {
            // if top left corner of active rect is inside droppable rect
            if (
              active.rect.current.translated &&
              rect.left <= active.rect.current.translated.left &&
              rect.top <= active.rect.current.translated.top &&
              rect.right >= active.rect.current.translated.left &&
              rect.bottom >= active.rect.current.translated.top
            ) {
              collisions.push({ id });
            }
          });
          collisions.sort((a, b) => {
            return displayedRoot
              ? (tryGetNodeDepth(displayedRoot, b.id.toString()) ?? -1) -
                  (tryGetNodeDepth(displayedRoot, a.id.toString()) ?? -1)
              : (() => {
                  throw new Error('root not set');
                })();
          });
          return collisions;
        }}
        measuring={measuring}
        onDragCancel={() => {
          reset();
        }}
        onDragEnd={() => {
          unGhostRoot(displayedRoot);
          setActualRoot(displayedRoot);
          setDraggedNode(undefined);
        }}
        onDragMove={({ active: dragged, over: dragOver }) => {
          if (!draggedNode) {
            throw new Error('Dragged node not set');
          }
          const newRoot = CalculateNewTree(
            dragged,
            dragOver,
            cloneTree(displayedRoot),
            cloneTree(actualRoot),
            cloneTree(draggedNode),
          );
          setDisplayedRoot(newRoot);
        }}
        onDragStart={e => {
          const activeId = e.active.id.toString();

          if (activeId === OR_NEW_ID) {
            const group = newOrNode();
            ghostNode(group);
            setDraggedNode(group);
          } else if (activeId === AND_NEW_ID) {
            const group = newAndNode();
            ghostNode(group);
            setDraggedNode(group);
          } else if (activeId === NOT_NEW_ID) {
            const node = newNotNode();
            ghostNode(node);
            setDraggedNode(node);
          } else if (activeId.startsWith(EXPRESSION_NEW_ID)) {
            const module_id = activeId.replace(EXPRESSION_NEW_ID, '');
            const node = newIncompleteExpressionNode(module_id, undefined);
            ghostNode(node);
            setDraggedNode(node);
          } else {
            if (!actualRoot) {
              throw new Error("onDragStart: root not set while dragging a node that's not new");
            }

            const node = findNode(actualRoot, activeId);
            ghostRoot(node);
            const nodeParent = node.parentUuid ? findNode(actualRoot, node.parentUuid) : undefined;
            const displayed_root = cloneTree(actualRoot);
            const actual_root = cloneTree(detachNode(actualRoot, node, nodeParent));
            setActualRoot(actual_root);
            setDisplayedRoot(displayed_root);
            setDraggedNode(cloneTree(node));
          }
        }}
        sensors={sensors}
      >
        {props.children}
      </DndContext>
    </ExpressionBuilderContext.Provider>
  );
};

const CalculateNewTree = (
  dragged: Active,
  dragOver: Over | null,
  displayedRoot: IdTreeExpression | undefined,
  actualRoot: IdTreeExpression | undefined,
  draggedNode: IdTreeExpression,
): IdTreeExpression | undefined => {
  if (!dragOver) {
    return displayedRoot;
  }

  if (dragOver.id.toString() === EMPTY_EXPRESSION_ID || !displayedRoot || !actualRoot) {
    const newNode = cloneTree(draggedNode);
    ghostRoot(newNode);
    return newNode;
  }

  const displayedDragOverNode = findNode(displayedRoot, dragOver.id.toString());
  if (displayedDragOverNode.isGhost) {
    return displayedRoot;
  }

  const dragOverNode = findNode(actualRoot, dragOver.id.toString());

  const edgePx = 10;
  const isTopEdge = dragged.rect.current.translated && dragged.rect.current.translated.top < dragOver.rect.top + edgePx;
  const isBottomEdge =
    dragged.rect.current.translated &&
    dragged.rect.current.translated.top > dragOver.rect.top + dragOver.rect.height - edgePx;
  const dragOverParent = dragOverNode.parentUuid ? findNode(actualRoot, dragOverNode.parentUuid) : null;

  if ((isTopEdge || isBottomEdge) && dragOverParent) {
    const relativeIdx = getParentRelativeIndex(actualRoot, dragOverNode, dragOverParent);

    if (isTopEdge) {
      return putNode(actualRoot, displayedRoot, draggedNode, dragOverParent, relativeIdx);
    } else if (isBottomEdge) {
      return putNode(actualRoot, displayedRoot, draggedNode, dragOverParent, relativeIdx + 1);
    }
  }
  return putNode(actualRoot, displayedRoot, draggedNode, dragOverNode, isBottomEdge ? 1 : 0);
};

/*
    This function contains the logic of what happens when a node is dropped on another node
    Considering type of the dragged and dropped node. It does not consider position of the drop.
    - If the node is dropped on a group(And/Or) node, it will be added as a child of the group node
    - If the node is dropped on a not node without a child, it will be added as a child of the not node
    - If the node is dropped on a not node with a child, a new 'or' group node will be created and the not node's child
      and the dragged node will be added as children of the new group node. The new group node will be added as a child
      of the not node
    - If the node is dropped on an expression node with a parent, it will be added as a child of the expression node's
      parent (This is the same as dropping on the parent node)
    - If the node is dropped on an expression node with no parent, a new 'or' group node will be created and the
      expression node and the dragged node will be added as children of the new group node. The new group node will be
      the new root node
  */
function putNode(
  actualRoot: IdTreeExpression,
  displayedRoot: IdTreeExpression,
  draggedNode: IdTreeExpression,
  dragOverNode: IdTreeExpression,
  parentRelativeIdx: number,
): IdTreeExpression {
  ghostRoot(draggedNode);
  switch (dragOverNode.operator) {
    case 'and':
    case 'or': {
      attachNode(actualRoot, draggedNode, dragOverNode, parentRelativeIdx);
      return actualRoot;
    }
    case 'incomplete_expression':
    case 'expression': {
      if (!dragOverNode.parentUuid) {
        const groupNode = (() => {
          //if a ghost OR node already exists we want to reuse the UUID
          const displayedDragOverNode = findNode(displayedRoot, dragOverNode.uuid);
          const displayedDragOverNodeParent = displayedDragOverNode.parentUuid
            ? findNode(displayedRoot, displayedDragOverNode.parentUuid)
            : undefined;
          if (
            displayedDragOverNodeParent &&
            displayedDragOverNodeParent.operator === 'or' &&
            displayedDragOverNodeParent.isGhost
          ) {
            return newOrNode(displayedDragOverNodeParent.uuid);
          } else {
            return newOrNode();
          }
        })();

        ghostNode(groupNode);
        if (parentRelativeIdx === 0) {
          attachNode(groupNode, draggedNode, groupNode, 0);
          attachNode(groupNode, dragOverNode, groupNode, 1);
        } else {
          attachNode(groupNode, dragOverNode, groupNode, 0);
          attachNode(groupNode, draggedNode, groupNode, 1);
        }
        return groupNode;
      }
      const newParent = findNode(actualRoot, dragOverNode.parentUuid);
      return putNode(
        actualRoot,
        displayedRoot,
        draggedNode,
        newParent,
        getParentRelativeIndex(actualRoot, dragOverNode, newParent),
      );
    }
    case 'not': {
      if (!dragOverNode.expr) {
        attachNode(actualRoot, draggedNode, dragOverNode, 0);
        return actualRoot;
      } else {
        const groupNode = (() => {
          //if a ghost OR node already exists we want to reuse the UUID
          const displayedDragOverNode = findNode(displayedRoot, dragOverNode.uuid);
          if (displayedDragOverNode.operator !== 'not') {
            throw new Error('displayedDragOverNode type mismatch from original root');
          }
          const displayedDragOverNodeChild = displayedDragOverNode.expr;
          if (
            displayedDragOverNodeChild &&
            displayedDragOverNodeChild.operator === 'or' &&
            displayedDragOverNodeChild.isGhost
          ) {
            return newOrNode(displayedDragOverNodeChild.uuid);
          } else {
            return newOrNode();
          }
        })();
        ghostNode(groupNode);
        const node = dragOverNode.expr;
        detachNode(actualRoot, node, dragOverNode);
        attachNode(actualRoot, groupNode, dragOverNode, 0);
        if (parentRelativeIdx === 0) {
          attachNode(groupNode, draggedNode, groupNode, 0);
          attachNode(groupNode, node, groupNode, 1);
        } else {
          attachNode(groupNode, node, groupNode, 0);
          attachNode(groupNode, draggedNode, groupNode, 1);
        }
        return actualRoot;
      }
    }
  }
}
